/**
 * Game Over Scene: displayed when the player loses all lives.
 *
 * Shows final stats (level reached, score, coins) and offers to return to the
 * title screen. In Explorer mode, also presents a CONTINUE option with a cost
 * (score penalty for free continues, coin penalty for paid continues).
 */

import Phaser from 'phaser';
import { SCENE_GAME_OVER, SCENE_GAME, SCENE_TITLE, GameMode } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  AUDIO_GAME_END_RESULTS,
  AUDIO_MENU_CLICK,
  AUDIO_MENU_SELECT,
  AUDIO_COIN,
  SCORE_TO_COINS_RATE,
  EXPLORER_FREE_CONTINUES,
  EXPLORER_SCORE_PENALTY,
  EXPLORER_COIN_PENALTY,
  EXPLORER_MIN_COIN_COST,
} from '../config/game-config.js';
import { deleteSave, loadGame, saveGame } from '../utils/save-manager.js';
import { addToWallet, clearConsumableItems, getWallet, setWallet } from '../systems/ShopManager.js';
import { getSfxVol, applyMasterMute, refreshConfig } from '../utils/audio-config.js';
import { addRecord } from '../utils/records-manager.js';
import { unlockAchievement } from '../utils/achievement-manager.js';
import { ACHIEVEMENT_RICH_WALLET } from '../config/game-config.js';

const TITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '32px',
  color: '#ffffff',
};

const STAT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#aaaaaa',
};

const WALLET_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#cccc88',
};

const PROMPT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#888888',
};

const COST_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#cc8888',
};

const OPTION_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#ffffff',
};

const OPTION_DIM_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#555555',
};

export default class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_GAME_OVER });
  }

  /**
   * @param {{
   *   level: number,
   *   score: number,
   *   coins: number,
   *   agmuths: number,
   *   timePlayed?: number,
   *   isFinalBattle?: boolean,
   *   gameMode?: string,
   *   explorerContinuesUsed?: number,
   * }} data
   */
  init(data) {
    this.finalLevel = data.level ?? 1;
    this.finalScore = data.score ?? 0;
    this.finalCoins = data.coins ?? 0;
    this.finalAgmuths = data.agmuths ?? 0;
    this.finalTimePlayed = data.timePlayed ?? 0;
    this.isFinalBattle = data.isFinalBattle ?? false;
    this._gameMode = data.gameMode ?? GameMode.CLASSIC;
    this._explorerContinuesUsed = data.explorerContinuesUsed ?? 0;
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');

    // Smooth fade-in from black
    this.cameras.main.fadeIn(400, 0, 0, 0);

    // Refresh audio/difficulty config from localStorage
    refreshConfig();
    applyMasterMute(this.sound);

    // Reset transition lock (scene instance is reused across restarts)
    this._transitioning = false;

    // Determine if Explorer continue is available
    const isExplorer = this._gameMode === GameMode.EXPLORER && !this.isFinalBattle;

    if (isExplorer) {
      // Explorer mode: defer save deletion and coin banking until player chooses QUIT
      this._createExplorerLayout();
    } else {
      // Classic mode / Final battle: immediate cleanup
      deleteSave();
      addToWallet(this.finalCoins);
      clearConsumableItems();

      // Save record to best-games history
      addRecord({
        score: this.finalScore,
        level: this.finalLevel,
        coins: this.finalCoins,
        agmuths: this.finalAgmuths,
        timePlayed: this.finalTimePlayed,
      });

      // Rich achievement: check wallet balance after banking coins
      if (getWallet() >= ACHIEVEMENT_RICH_WALLET) {
        unlockAchievement('rich');
      }

      this._createClassicLayout();
    }
  }

  // ---------------------------------------------------------------
  // Classic mode layout (original behavior)
  // ---------------------------------------------------------------

  /** @private Build the classic game over screen (stats + ENTER to return). */
  _createClassicLayout() {
    const walletBalance = getWallet();

    // Play game-end results sound
    this._playResultsSound();

    // Decorative border
    this._drawBorder();

    const cx = GAME_WIDTH / 2;

    // Title
    const titleText = this.isFinalBattle ? 'DEFEATED BY VORATH' : 'GAME OVER';
    this.add.text(cx, 180, titleText, TITLE_STYLE).setOrigin(0.5);

    // Decorative line
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.3);
    g.lineBetween(cx - 100, 210, cx + 100, 210);

    // Stats — sequential reveal with delays for dramatic effect
    const statItems = [
      { y: 250, text: `Level reached: ${this.finalLevel}` },
      { y: 280, text: `Final score: ${this.finalScore}` },
      { y: 310, text: `Coins collected: ${this.finalCoins}` },
    ];

    // Show Agmuth count only if the player found at least one
    if (this.finalAgmuths > 0) {
      statItems.push({ y: 340, text: `Agmuths found: ${this.finalAgmuths}` });
    }

    // Wallet balance line (always shown)
    const walletY = statItems[statItems.length - 1].y + 30;
    statItems.push({ y: walletY, text: `Wallet: ${walletBalance} coins`, style: 'wallet' });

    this._animateStats(statItems, cx);

    // Prompt — appears after all stats have revealed
    const promptDelay = 300 + statItems.length * 400 + 200;
    const lastStatY = statItems[statItems.length - 1].y;
    const promptY = lastStatY + 50;
    const prompt = this.add.text(cx, promptY, 'Press ENTER to return to title', PROMPT_STYLE)
      .setOrigin(0.5);
    prompt.setAlpha(0);

    this.time.delayedCall(promptDelay, () => {
      if (!prompt.active) return;
      prompt.setAlpha(1);
      this._blinkText(prompt);
    });

    // Mouse: clicking anywhere on screen returns to title (after prompt appears)
    this.input.on('pointerdown', () => {
      this._beginTransition();
    });

    // Keyboard input
    this.input.keyboard.on('keydown-ENTER', () => {
      this._beginTransition();
    });
    this.input.keyboard.on('keydown-SPACE', () => {
      this._beginTransition();
    });
  }

  // ---------------------------------------------------------------
  // Explorer mode layout
  // ---------------------------------------------------------------

  /** @private Build the Explorer mode game over screen with CONTINUE / QUIT. */
  _createExplorerLayout() {
    // Play game-end results sound
    this._playResultsSound();

    // Decorative border
    this._drawBorder();

    const cx = GAME_WIDTH / 2;

    // Title
    this.add.text(cx, 120, 'GAME OVER', TITLE_STYLE).setOrigin(0.5);

    // Mode indicator
    this.add.text(cx, 148, '- Explorer Mode -', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: '#888888',
    }).setOrigin(0.5);

    // Decorative line
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.3);
    g.lineBetween(cx - 100, 162, cx + 100, 162);

    // Stats — compact layout
    const statItems = [
      { y: 182, text: `Level reached: ${this.finalLevel}` },
      { y: 204, text: `Score: ${this.finalScore}` },
      { y: 226, text: `Coins collected: ${this.finalCoins}` },
    ];

    if (this.finalAgmuths > 0) {
      statItems.push({ y: 248, text: `Agmuths found: ${this.finalAgmuths}` });
    }

    this._animateStats(statItems, cx);

    // --- Calculate continue cost ---
    const continueInfo = this._calculateContinueCost();
    const canContinue = continueInfo.canContinue;

    // Cost display — appears after stats
    const costBaseY = statItems[statItems.length - 1].y + 30;
    const costDelay = 300 + statItems.length * 400 + 100;

    // Score penalty line (always shown)
    const scorePenaltyLabel = `Score penalty: -${continueInfo.scoreCost} (${Math.round(EXPLORER_SCORE_PENALTY * 100)}%)`;
    const scorePenaltyText = this.add.text(cx, costBaseY, scorePenaltyLabel, COST_STYLE)
      .setOrigin(0.5).setAlpha(0);

    this.time.delayedCall(costDelay, () => {
      if (!scorePenaltyText.active) return;
      this.tweens.add({
        targets: scorePenaltyText,
        alpha: 1,
        y: costBaseY,
        duration: 300,
        ease: 'Quad.easeOut',
        onStart: () => scorePenaltyText.setY(costBaseY + 8),
      });
    });

    // Track vertical offset for dynamic layout
    let nextY = costBaseY + 18;

    // Coin cost line (only for paid continues)
    if (continueInfo.isPaid) {
      const coinLabel = canContinue
        ? `Vorath's toll: ${continueInfo.coinCost} coins (${Math.round(EXPLORER_COIN_PENALTY * 100)}% of ${continueInfo.totalCoins})`
        : `Not enough coins (need at least ${EXPLORER_MIN_COIN_COST})`;
      const coinCostText = this.add.text(cx, nextY, coinLabel, COST_STYLE)
        .setOrigin(0.5).setAlpha(0);

      this.time.delayedCall(costDelay + 200, () => {
        if (!coinCostText.active) return;
        this.tweens.add({
          targets: coinCostText,
          alpha: 1,
          y: nextY,
          duration: 300,
          ease: 'Quad.easeOut',
          onStart: () => coinCostText.setY(nextY + 8),
        });
      });
      nextY += 18;
    }

    // Lore text — Vorath's toll flavor
    const loreMessages = [
      '"The labyrinth does not forgive freely..."',
      '"Every breath here has a price, wanderer..."',
      '"Vorath savors your desperation..."',
      '"The walls remember your failure..."',
      '"Death is but a toll in my domain..."',
    ];
    const loreMsg = loreMessages[Math.floor(Math.random() * loreMessages.length)];
    const loreText = this.add.text(cx, nextY + 4, loreMsg, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: '#884444',
      fontStyle: 'italic',
    }).setOrigin(0.5).setAlpha(0);

    this.time.delayedCall(costDelay + 400, () => {
      if (!loreText.active) return;
      this.tweens.add({
        targets: loreText,
        alpha: 0.8,
        duration: 600,
        ease: 'Quad.easeOut',
      });
    });

    nextY += 22;

    // Free continues remaining indicator
    const freeContinuesRemaining = Math.max(0, EXPLORER_FREE_CONTINUES - this._explorerContinuesUsed);
    if (freeContinuesRemaining > 0) {
      const freeText = this.add.text(cx, nextY,
        `(${freeContinuesRemaining} free continue${freeContinuesRemaining > 1 ? 's' : ''} remaining)`, {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '11px',
          color: '#668866',
        }).setOrigin(0.5).setAlpha(0);

      this.time.delayedCall(costDelay + 500, () => {
        if (!freeText.active) return;
        this.tweens.add({
          targets: freeText,
          alpha: 1,
          duration: 300,
          ease: 'Quad.easeOut',
        });
      });
      nextY += 18;
    }

    // --- CONTINUE / QUIT options ---
    const optionsY = nextY + 15;
    const optionDelay = costDelay + 700;

    // Separator line
    const g2 = this.add.graphics();
    g2.lineStyle(1, COLOR_WHITE, 0.15);
    g2.lineBetween(cx - 80, optionsY - 8, cx + 80, optionsY - 8);
    g2.setAlpha(0);
    this.time.delayedCall(optionDelay - 100, () => {
      if (!g2.active) return;
      g2.setAlpha(1);
    });

    // Option 0: CONTINUE
    const continueStyle = canContinue ? OPTION_STYLE : OPTION_DIM_STYLE;
    const continueLabel = canContinue ? 'CONTINUE' : 'CONTINUE (cannot afford)';
    const continueText = this.add.text(cx, optionsY + 5, continueLabel, continueStyle)
      .setOrigin(0.5).setAlpha(0);

    // Option 1: QUIT
    const quitText = this.add.text(cx, optionsY + 32, 'QUIT', OPTION_STYLE)
      .setOrigin(0.5).setAlpha(0);

    // Selector arrow
    this._explorerOptions = [
      { text: continueText, action: 'continue', enabled: canContinue },
      { text: quitText, action: 'quit', enabled: true },
    ];
    this._explorerSelectedIndex = canContinue ? 0 : 1; // default to CONTINUE if available

    const arrowX = cx - 80;
    const selectedOption = this._explorerOptions[this._explorerSelectedIndex];
    this._selectorArrow = this.add.text(arrowX, selectedOption.text.y, '>', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0, 0.5).setAlpha(0);

    // Mouse interaction for CONTINUE / QUIT options
    this._explorerOptions.forEach((opt, i) => {
      if (!opt.enabled) return;
      opt.text.setInteractive({ useHandCursor: true });
      opt.text.on('pointerover', () => {
        if (!this._explorerInputReady || this._transitioning) return;
        if (this._explorerSelectedIndex !== i) {
          this._explorerSelectedIndex = i;
          try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.3) }); } catch (_) { /* */ }
          this._selectorArrow.setY(opt.text.y);
        }
      });
      opt.text.on('pointerdown', () => {
        if (!this._explorerInputReady || this._transitioning) return;
        this._explorerSelectedIndex = i;
        this._confirmExplorer();
      });
    });

    // Reveal options after stats
    this.time.delayedCall(optionDelay, () => {
      for (const opt of this._explorerOptions) {
        if (!opt.text.active) return;
      }
      continueText.setAlpha(1);
      quitText.setAlpha(1);
      this._selectorArrow.setAlpha(1);
      this._explorerInputReady = true;
    });

    // Keyboard input
    this._explorerInputReady = false;

    this.input.keyboard.on('keydown-UP', () => {
      this._navigateExplorer(-1);
    });
    this.input.keyboard.on('keydown-DOWN', () => {
      this._navigateExplorer(1);
    });
    this.input.keyboard.on('keydown-ENTER', () => {
      this._confirmExplorer();
    });
    this.input.keyboard.on('keydown-SPACE', () => {
      this._confirmExplorer();
    });
  }

  /**
   * Calculate the cost of continuing in Explorer mode.
   *
   * - **Free continues** (first {@link EXPLORER_FREE_CONTINUES}): only a score
   *   penalty is applied ({@link EXPLORER_SCORE_PENALTY} fraction of current score).
   * - **Paid continues** (after free ones are exhausted): the same score penalty
   *   PLUS a coin cost ({@link EXPLORER_COIN_PENALTY} fraction of total coins,
   *   minimum {@link EXPLORER_MIN_COIN_COST}). If total coins are below the
   *   minimum, the player cannot continue.
   *
   * @private
   * @returns {{
   *   canContinue: boolean,
   *   scoreCost: number,
   *   isPaid: boolean,
   *   coinCost: number,
   *   totalCoins: number,
   * }}
   */
  _calculateContinueCost() {
    const scoreCost = Math.floor(this.finalScore * EXPLORER_SCORE_PENALTY);
    const isPaid = this._explorerContinuesUsed >= EXPLORER_FREE_CONTINUES;

    if (!isPaid) {
      // Free continue — score penalty only
      return {
        canContinue: true,
        scoreCost,
        isPaid: false,
        coinCost: 0,
        totalCoins: 0,
      };
    }

    // Paid continue — score penalty + coin cost
    const walletBalance = getWallet();
    const totalCoins = this.finalCoins + walletBalance;
    const coinCost = Math.max(
      EXPLORER_MIN_COIN_COST,
      Math.floor(totalCoins * EXPLORER_COIN_PENALTY),
    );

    if (totalCoins < EXPLORER_MIN_COIN_COST) {
      return {
        canContinue: false,
        scoreCost,
        isPaid: true,
        coinCost: 0,
        totalCoins,
      };
    }

    return {
      canContinue: true,
      scoreCost,
      isPaid: true,
      coinCost,
      totalCoins,
    };
  }

  /**
   * Navigate the Explorer mode option selector.
   * @private
   * @param {number} dir — -1 for up, +1 for down
   */
  _navigateExplorer(dir) {
    if (!this._explorerInputReady || this._transitioning) return;

    // Find next enabled option
    let newIndex = this._explorerSelectedIndex;
    const count = this._explorerOptions.length;

    for (let i = 0; i < count; i++) {
      newIndex = (newIndex + dir + count) % count;
      if (this._explorerOptions[newIndex].enabled) break;
    }

    if (newIndex === this._explorerSelectedIndex) return;

    this._explorerSelectedIndex = newIndex;

    try {
      this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.3) });
    } catch (_) { /* audio fallback */ }

    // Move selector arrow
    const targetY = this._explorerOptions[newIndex].text.y;
    this._selectorArrow.setY(targetY);
  }

  /**
   * Confirm the selected Explorer mode option (CONTINUE or QUIT).
   * @private
   */
  _confirmExplorer() {
    if (!this._explorerInputReady || this._transitioning) return;

    const selected = this._explorerOptions[this._explorerSelectedIndex];
    if (!selected.enabled) return;

    if (selected.action === 'continue') {
      this._explorerContinue();
    } else {
      this._explorerQuit();
    }
  }

  /**
   * Handle CONTINUE in Explorer mode: apply penalties, update save, restart game.
   *
   * Score penalty is **always** applied (both free and paid continues).
   * Coin penalty is applied **only** for paid continues (after free ones are
   * exhausted). Coin cost is deducted from run coins first, then from the
   * persistent wallet.
   * @private
   */
  _explorerContinue() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Recalculate cost (in case of edge cases)
    const costInfo = this._calculateContinueCost();
    if (!costInfo.canContinue) return;

    // Load the save that GameScene wrote before transitioning here
    const save = loadGame();
    if (!save) {
      // Fallback: save is corrupted/missing — treat as quit
      this._explorerQuit();
      return;
    }

    // 1) Score penalty — always applied
    save.score = Math.max(0, (save.score || 0) - costInfo.scoreCost);

    // 2) Coin penalty — only for paid continues
    if (costInfo.isPaid && costInfo.coinCost > 0) {
      let remaining = costInfo.coinCost;
      // Deduct from run coins first
      const runCoins = save.coinsCollected || 0;
      const runDeduction = Math.min(runCoins, remaining);
      save.coinsCollected = runCoins - runDeduction;
      remaining -= runDeduction;
      // Deduct remainder from wallet
      if (remaining > 0) {
        const wallet = getWallet();
        setWallet(Math.max(0, wallet - remaining));
      }
    }

    // Increment continues used
    save.explorerContinuesUsed = (save.explorerContinuesUsed || 0) + 1;

    // Write updated save
    saveGame(save);

    // Transition to GameScene with continuing=true
    this._stopResultsSound();
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_GAME, { continuing: true });
    });
  }

  /**
   * Handle QUIT in Explorer mode: bank coins, clear save, return to title.
   * @private
   */
  _explorerQuit() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Standard game over cleanup: delete save, bank coins, clear consumables
    deleteSave();
    addToWallet(this.finalCoins);
    clearConsumableItems();

    // Save record to best-games history
    addRecord({
      score: this.finalScore,
      level: this.finalLevel,
      coins: this.finalCoins,
      agmuths: this.finalAgmuths,
      timePlayed: this.finalTimePlayed,
    });

    // Rich achievement: check wallet balance after banking coins
    if (getWallet() >= ACHIEVEMENT_RICH_WALLET) {
      unlockAchievement('rich');
    }

    // Show score-to-coins conversion, then transition to title
    this._showScoreConversion(() => {
      this._stopResultsSound();
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(SCENE_TITLE);
      });
    });
  }

  // ---------------------------------------------------------------
  // Score-to-Coins Conversion
  // ---------------------------------------------------------------

  /**
   * Begin the transition to title (Classic mode).
   * Shows score-to-coins conversion first, then fades to title.
   * @private
   */
  _beginTransition() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    this._showScoreConversion(() => {
      this._stopResultsSound();
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(SCENE_TITLE);
      });
    });
  }

  /**
   * Display an animated score-to-coins conversion counter.
   *
   * The player's final score is converted into bonus wallet coins at
   * {@link SCORE_TO_COINS_RATE} (score points per coin). A counting animation
   * shows the score draining while coins accumulate, with coin sounds.
   *
   * If the score yields zero bonus coins, the callback fires immediately.
   *
   * @private
   * @param {() => void} onComplete — called when the animation finishes
   */
  _showScoreConversion(onComplete) {
    const bonusCoins = Math.floor(this.finalScore / SCORE_TO_COINS_RATE);

    if (bonusCoins <= 0) {
      // No bonus coins — skip animation
      if (onComplete) onComplete();
      return;
    }

    const cx = GAME_WIDTH / 2;
    const baseY = 440;

    // Separator line above conversion area
    const sepG = this.add.graphics();
    sepG.lineStyle(1, COLOR_WHITE, 0.2);
    sepG.lineBetween(cx - 120, baseY - 12, cx + 120, baseY - 12);
    sepG.setAlpha(0);

    // "SCORE BONUS" header
    const headerText = this.add.text(cx, baseY, 'SCORE BONUS', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '13px',
      color: '#cccc88',
    }).setOrigin(0.5).setAlpha(0);

    // Score remaining display
    const scoreText = this.add.text(cx - 60, baseY + 22, `Score: ${this.finalScore}`, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '14px',
      color: '#aaaaaa',
    }).setOrigin(0, 0.5).setAlpha(0);

    // Coins earned display
    const coinsText = this.add.text(cx - 60, baseY + 44, 'Coins: +0', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '14px',
      color: '#ffdd66',
    }).setOrigin(0, 0.5).setAlpha(0);

    // Rate label
    const rateText = this.add.text(cx, baseY + 68, `(${SCORE_TO_COINS_RATE} score = 1 coin)`, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#666666',
    }).setOrigin(0.5).setAlpha(0);

    // Fade in all elements
    const fadeTargets = [sepG, headerText, scoreText, coinsText, rateText];
    fadeTargets.forEach((t) => {
      this.tweens.add({
        targets: t,
        alpha: 1,
        duration: 300,
        ease: 'Quad.easeOut',
      });
    });

    // Counting animation
    // Determine speed: finish in ~2s max, but at least 30ms per tick
    const totalTicks = bonusCoins;
    const maxDuration = 2000;                         // ms for the full count
    const minInterval = 30;                           // ms minimum per tick
    const interval = Math.max(minInterval, Math.floor(maxDuration / totalTicks));

    // Coin sound rate-limiting: play at most every 80ms
    const coinSoundMinInterval = 80;
    let lastCoinSoundTime = 0;

    let currentCoins = 0;
    let currentScore = this.finalScore;
    const scorePerCoin = SCORE_TO_COINS_RATE;

    this.time.addEvent({
      delay: interval,
      repeat: totalTicks - 1,
      callback: () => {
        // Increment coins by 1, decrement score by rate
        currentCoins++;
        currentScore = Math.max(0, currentScore - scorePerCoin);

        if (scoreText.active) {
          scoreText.setText(`Score: ${currentScore}`);
        }
        if (coinsText.active) {
          coinsText.setText(`Coins: +${currentCoins}`);
        }

        // Play coin sound (rate-limited)
        const now = Date.now();
        if (now - lastCoinSoundTime >= coinSoundMinInterval) {
          lastCoinSoundTime = now;
          try {
            this.sound.play(AUDIO_COIN, { volume: getSfxVol(0.25) });
          } catch (_) { /* audio fallback */ }
        }

        // On last tick, bank bonus and transition
        if (currentCoins >= totalTicks) {
          this._finishScoreConversion(bonusCoins, coinsText, cx, baseY, onComplete);
        }
      },
    });
  }

  /**
   * Completion handler for score-to-coins conversion: banks bonus coins,
   * shows wallet total, then invokes the transition callback.
   * @private
   */
  _finishScoreConversion(bonusCoins, coinsText, cx, baseY, onComplete) {
    // Bank the bonus coins
    addToWallet(bonusCoins);

    // Flash the final coins text
    if (coinsText.active) {
      coinsText.setText(`Coins: +${bonusCoins}`);
      this.tweens.add({
        targets: coinsText,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 150,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }

    // Show wallet total
    const walletTotal = getWallet();
    const walletText = this.add.text(cx, baseY + 90, `Wallet: ${walletTotal} coins`, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: '#cccc88',
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: walletText,
      alpha: 1,
      duration: 300,
      ease: 'Quad.easeOut',
    });

    // Brief pause before transitioning
    this.time.delayedCall(800, () => {
      if (onComplete) onComplete();
    });
  }

  // ---------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------

  /**
   * Animate stat text items with staggered fade-in.
   * @private
   * @param {Array<{y: number, text: string, style?: string}>} statItems
   * @param {number} cx — center X
   */
  _animateStats(statItems, cx) {
    statItems.forEach((item, index) => {
      const style = item.style === 'wallet' ? WALLET_STYLE : STAT_STYLE;
      const stat = this.add.text(cx, item.y, item.text, style).setOrigin(0.5);
      stat.setAlpha(0);

      // Staggered fade-in: each stat appears 400ms after the previous
      this.tweens.add({
        targets: stat,
        alpha: 1,
        y: item.y,
        duration: 300,
        delay: 300 + index * 400,
        ease: 'Quad.easeOut',
        onStart: () => {
          // Slight slide up from 8px below
          stat.setY(item.y + 8);
        },
      });
    });
  }

  /**
   * Create a blinking tween on a text object.
   * @private
   * @param {Phaser.GameObjects.Text} text
   */
  _blinkText(text) {
    this.tweens.add({
      targets: text,
      alpha: 0.3,
      duration: 600,
      yoyo: true,
      repeat: -1,
    });
  }

  /** @private Play the game-end results sound. */
  _playResultsSound() {
    try {
      this._resultsSound = this.sound.add(AUDIO_GAME_END_RESULTS, { volume: getSfxVol(0.55) });
      this._resultsSound.play();
    } catch (_) {
      /* audio fallback — don't let audio failure break the Game Over screen */
    }
  }

  /** @private Stop the results sound if still playing. */
  _stopResultsSound() {
    if (this._resultsSound) {
      if (this._resultsSound.isPlaying) {
        this._resultsSound.stop();
      }
      this._resultsSound.destroy();
      this._resultsSound = null;
    }
  }

  /** @private Draw the decorative border matching TitleScene. */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }
}
